import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapContainer, TileLayer, Polygon, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

import { geofencesApi, companiesApi, devicesApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { ConfirmDialog } from '../../components/common';

interface Geofence {
  id: string;
  name: string;
  geometry: {
    type: 'Polygon' | 'Circle';
    coordinates?: number[][][];
    center?: [number, number];
    radius?: number;
  };
  alertRadius: number;
  isActive: boolean;
  createdAt: string;
  company: {
    id: string;
    name: string;
    code: string;
  };
  _count?: {
    devices: number;
  };
}

interface Company {
  id: string;
  name: string;
  code: string;
}

interface Device {
  id: string;
  imei: string;
  name: string | null;
}

interface GeofenceFormData {
  name: string;
  companyId: string;
  geometry: {
    type: 'Polygon' | 'Circle';
    coordinates?: number[][][];
    center?: [number, number];
    radius?: number;
  };
  alertRadius: number;
  isActive: boolean;
}

// Component to handle map click for drawing
function DrawHandler({
  isDrawing,
  onPointAdd,
}: {
  isDrawing: boolean;
  onPointAdd: (latlng: L.LatLng) => void;
}) {
  useMapEvents({
    click(e) {
      if (isDrawing) {
        onPointAdd(e.latlng);
      }
    },
  });
  return null;
}

// Component to fit bounds
function FitBounds({ geofence }: { geofence: Geofence | null }) {
  const map = useMap();

  useEffect(() => {
    if (geofence) {
      if (geofence.geometry.type === 'Circle' && geofence.geometry.center) {
        map.setView(geofence.geometry.center, 14);
      } else if (geofence.geometry.coordinates) {
        const coords = geofence.geometry.coordinates[0].map(
          (c) => [c[1], c[0]] as [number, number]
        );
        const bounds = L.latLngBounds(coords);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [geofence, map]);

  return null;
}

export function GeofencesPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<Geofence | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Geofence | null>(null);
  const [selectedGeofence, setSelectedGeofence] = useState<Geofence | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState<L.LatLng[]>([]);
  const [geometryType, setGeometryType] = useState<'Polygon' | 'Circle'>('Polygon');
  const [circleRadius, setCircleRadius] = useState(500);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [formData, setFormData] = useState<GeofenceFormData>({
    name: '',
    companyId: '',
    geometry: { type: 'Polygon', coordinates: [] },
    alertRadius: 5,
    isActive: true,
  });

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPERADMIN';

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesApi.list({ limit: 100 });
      return response.data;
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['geofences', selectedCompany],
    queryFn: async () => {
      const params: Record<string, unknown> = { limit: 100 };
      if (selectedCompany) params.companyId = selectedCompany;
      const response = await geofencesApi.list(params);
      return response.data;
    },
  });

  const { data: devicesData } = useQuery({
    queryKey: ['devices', { companyId: selectedGeofence?.company.id, limit: 100 }],
    queryFn: async () => {
      if (!selectedGeofence) return [] as Device[];
      const response = await devicesApi.list({ companyId: selectedGeofence.company.id, limit: 100 });
      // All queries under the ['devices', ...] key MUST return Device[] —
      // pages sharing the key receive each other's cached data as-is.
      const list = response.data.data;
      return (Array.isArray(list) ? list : list?.devices || []) as Device[];
    },
    enabled: !!selectedGeofence,
  });

  const createMutation = useMutation({
    mutationFn: (data: GeofenceFormData) => geofencesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      closeModal();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<GeofenceFormData> }) =>
      geofencesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => geofencesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      setSelectedGeofence(null);
    },
  });

  const assignDevicesMutation = useMutation({
    mutationFn: ({ id, deviceIds }: { id: string; deviceIds: string[] }) =>
      geofencesApi.assignDevices(id, deviceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['geofences'] });
      setIsDeviceModalOpen(false);
    },
  });

  const companies = (companiesData?.data || []) as Company[];
  const geofences = (data?.data || []) as Geofence[];
  const devices = devicesData ?? [];

  const openModal = (geofence?: Geofence) => {
    if (geofence) {
      setEditingGeofence(geofence);
      setFormData({
        name: geofence.name,
        companyId: geofence.company.id,
        geometry: geofence.geometry,
        alertRadius: geofence.alertRadius,
        isActive: geofence.isActive,
      });
      setGeometryType(geofence.geometry.type);
      if (geofence.geometry.type === 'Polygon' && geofence.geometry.coordinates) {
        setDrawingPoints(
          geofence.geometry.coordinates[0].map(
            (c) => L.latLng(c[1], c[0])
          )
        );
      } else if (geofence.geometry.type === 'Circle' && geofence.geometry.center) {
        setDrawingPoints([L.latLng(geofence.geometry.center[0], geofence.geometry.center[1])]);
        setCircleRadius(geofence.geometry.radius || 500);
      }
    } else {
      setEditingGeofence(null);
      setFormData({
        name: '',
        companyId: companies[0]?.id || '',
        geometry: { type: 'Polygon', coordinates: [] },
        alertRadius: 5,
        isActive: true,
      });
      setDrawingPoints([]);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingGeofence(null);
    setDrawingPoints([]);
    setIsDrawing(false);
  };

  const handlePointAdd = (latlng: L.LatLng) => {
    if (geometryType === 'Circle') {
      setDrawingPoints([latlng]);
    } else {
      setDrawingPoints((prev) => [...prev, latlng]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let geometry: GeofenceFormData['geometry'];
    if (geometryType === 'Circle' && drawingPoints.length === 1) {
      geometry = {
        type: 'Circle',
        center: [drawingPoints[0].lat, drawingPoints[0].lng],
        radius: circleRadius,
      };
    } else if (geometryType === 'Polygon' && drawingPoints.length >= 3) {
      geometry = {
        type: 'Polygon',
        coordinates: [drawingPoints.map((p) => [p.lng, p.lat])],
      };
    } else {
      alert('Dibuje la geocerca en el mapa primero');
      return;
    }

    const submitData = { ...formData, geometry };

    if (editingGeofence) {
      updateMutation.mutate({ id: editingGeofence.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const openDeviceModal = (geofence: Geofence) => {
    setSelectedGeofence(geofence);
    setSelectedDevices([]);
    setIsDeviceModalOpen(true);
  };

  const handleAssignDevices = () => {
    if (selectedGeofence && selectedDevices.length > 0) {
      assignDevicesMutation.mutate({
        id: selectedGeofence.id,
        deviceIds: selectedDevices,
      });
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-6 pb-0">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Geocercas</h1>
            <p className="text-gray-600">Gestión de areas geograficas</p>
          </div>
          <button
            onClick={() => openModal()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Nueva Geocerca
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg p-4 mb-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Empresa
              </label>
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Map and List */}
      <div className="flex-1 flex p-6 pt-0 gap-4">
        {/* Map */}
        <div className="flex-1 bg-white shadow rounded-lg overflow-hidden">
          <MapContainer
            center={[18.4861, -69.9312]}
            zoom={10}
            className="h-full w-full"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Show all geofences */}
            {geofences.map((geofence) => {
              if (geofence.geometry.type === 'Polygon' && geofence.geometry.coordinates) {
                const positions = geofence.geometry.coordinates[0].map(
                  (c) => [c[1], c[0]] as [number, number]
                );
                return (
                  <Polygon
                    key={geofence.id}
                    positions={positions}
                    pathOptions={{
                      color: selectedGeofence?.id === geofence.id ? '#3b82f6' : '#6b7280',
                      fillOpacity: 0.2,
                    }}
                    eventHandlers={{
                      click: () => setSelectedGeofence(geofence),
                    }}
                  />
                );
              } else if (geofence.geometry.type === 'Circle' && geofence.geometry.center) {
                return (
                  <Circle
                    key={geofence.id}
                    center={geofence.geometry.center}
                    radius={geofence.geometry.radius || 500}
                    pathOptions={{
                      color: selectedGeofence?.id === geofence.id ? '#3b82f6' : '#6b7280',
                      fillOpacity: 0.2,
                    }}
                    eventHandlers={{
                      click: () => setSelectedGeofence(geofence),
                    }}
                  />
                );
              }
              return null;
            })}

            <FitBounds geofence={selectedGeofence} />
          </MapContainer>
        </div>

        {/* List */}
        <div className="w-80 bg-white shadow rounded-lg overflow-hidden flex flex-col">
          <div className="p-4 border-b">
            <h3 className="font-medium">Lista de Geocercas</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">Cargando...</div>
            ) : geofences.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No hay geocercas
              </div>
            ) : (
              <div className="divide-y">
                {geofences.map((geofence) => (
                  <div
                    key={geofence.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${
                      selectedGeofence?.id === geofence.id ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => setSelectedGeofence(geofence)}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium">{geofence.name}</span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          geofence.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {geofence.isActive ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {geofence.company.code} - {geofence.geometry.type}
                    </div>
                    <div className="text-xs text-gray-500">
                      {geofence._count?.devices || 0}{' '}
                      {(geofence._count?.devices || 0) === 1 ? 'dispositivo' : 'dispositivos'}
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(geofence);
                        }}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Editar
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeviceModal(geofence);
                        }}
                        className="text-xs text-green-600 hover:underline"
                      >
                        Dispositivos
                      </button>
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(geofence);
                          }}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Eliminar geocerca"
        message={`Esta seguro de eliminar la geocerca "${deleteTarget?.name}"? Esta accion no se puede deshacer.`}
        confirmLabel="Eliminar"
        destructive
        isLoading={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl h-[80vh] flex flex-col">
            <h2 className="text-xl font-bold mb-4">
              {editingGeofence ? 'Editar Geocerca' : 'Nueva Geocerca'}
            </h2>
            <div className="flex-1 flex gap-4 min-h-0">
              {/* Form */}
              <div className="w-64 flex-shrink-0">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Empresa *
                    </label>
                    <select
                      value={formData.companyId}
                      onChange={(e) =>
                        setFormData({ ...formData, companyId: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Seleccionar...</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo
                    </label>
                    <select
                      value={geometryType}
                      onChange={(e) => {
                        setGeometryType(e.target.value as 'Polygon' | 'Circle');
                        setDrawingPoints([]);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Polygon">Poligono</option>
                      <option value="Circle">Circulo</option>
                    </select>
                  </div>
                  {geometryType === 'Circle' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Radio (metros)
                      </label>
                      <input
                        type="number"
                        value={circleRadius}
                        onChange={(e) => setCircleRadius(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min={10}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Radio de alerta (km)
                    </label>
                    <input
                      type="number"
                      value={formData.alertRadius}
                      onChange={(e) =>
                        setFormData({ ...formData, alertRadius: Number(e.target.value) })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min={0}
                      step={0.1}
                    />
                  </div>
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) =>
                          setFormData({ ...formData, isActive: e.target.checked })
                        }
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Activa
                      </span>
                    </label>
                  </div>
                  <div className="pt-4 border-t flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsDrawing(!isDrawing);
                        if (!isDrawing) setDrawingPoints([]);
                      }}
                      className={`w-full px-4 py-2 rounded-md ${
                        isDrawing
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {isDrawing ? 'Cancelar dibujo' : 'Dibujar en mapa'}
                    </button>
                    <div className="text-xs text-gray-500">
                      {geometryType === 'Polygon'
                        ? `Puntos: ${drawingPoints.length}`
                        : drawingPoints.length > 0
                        ? 'Centro seleccionado'
                        : 'Click en el mapa para centro'}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      Guardar
                    </button>
                  </div>
                </form>
              </div>

              {/* Map */}
              <div className="flex-1 rounded-lg overflow-hidden border">
                <MapContainer
                  center={[18.4861, -69.9312]}
                  zoom={10}
                  className="h-full w-full"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <DrawHandler
                    isDrawing={isDrawing}
                    onPointAdd={handlePointAdd}
                  />
                  {/* Show drawing preview */}
                  {geometryType === 'Polygon' && drawingPoints.length >= 3 && (
                    <Polygon
                      positions={drawingPoints.map((p) => [p.lat, p.lng] as [number, number])}
                      pathOptions={{ color: '#3b82f6', fillOpacity: 0.3 }}
                    />
                  )}
                  {geometryType === 'Circle' && drawingPoints.length === 1 && (
                    <Circle
                      center={[drawingPoints[0].lat, drawingPoints[0].lng]}
                      radius={circleRadius}
                      pathOptions={{ color: '#3b82f6', fillOpacity: 0.3 }}
                    />
                  )}
                </MapContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Devices Modal */}
      {isDeviceModalOpen && selectedGeofence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              Asignar dispositivos a {selectedGeofence.name}
            </h2>
            <div className="max-h-64 overflow-y-auto mb-4">
              {devices.length === 0 ? (
                <p className="text-gray-500">No hay dispositivos disponibles</p>
              ) : (
                devices.map((device) => (
                  <label key={device.id} className="flex items-center p-2 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={selectedDevices.includes(device.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedDevices((prev) => [...prev, device.id]);
                        } else {
                          setSelectedDevices((prev) =>
                            prev.filter((id) => id !== device.id)
                          );
                        }
                      }}
                      className="mr-3"
                    />
                    <div>
                      <div className="text-sm font-medium">
                        {device.name || 'Sin nombre'}
                      </div>
                      <div className="text-xs text-gray-500">{device.imei}</div>
                    </div>
                  </label>
                ))
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsDeviceModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleAssignDevices}
                disabled={selectedDevices.length === 0 || assignDevicesMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Asignar ({selectedDevices.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
